import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Facebook, Instagram, Mail } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HERO_BG =
  "https://images.unsplash.com/photo-1480563597043-1c877e682fc7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxiYXNlYmFsbCUyMGFjdGlvbiUyMG5pZ2h0fGVufDB8fHx8MTc5MTQyNzQ4Nnww&ixlib=rb-4.1.0&q=85";

const line = {
  hidden: { y: "112%", rotate: 3 },
  show: (i) => ({
    y: "0%",
    rotate: 0,
    transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative flex min-h-screen items-end overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_BG}
          alt=""
          className="h-full w-full scale-110 object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36 md:px-10 md:pb-28"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-6 text-[10px] font-semibold uppercase tracking-[0.35em] text-accentlight md:text-xs"
        >
          Baseball &bull; Action &bull; Memories
        </motion.p>

        <h1 className="font-display leading-[0.85]" style={{ perspective: 800 }}>
          <span className="block overflow-hidden">
            <motion.span
              custom={0}
              variants={line}
              initial="hidden"
              animate="show"
              className="block origin-left text-[clamp(4.2rem,17vw,13.5rem)]"
            >
              <span className="text-white text-glow">GAME</span>
              <span className="bg-gradient-to-r from-accent to-accentlight bg-clip-text text-transparent">
                LENS
              </span>
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              custom={1}
              variants={line}
              initial="hidden"
              animate="show"
              className="block origin-left text-[clamp(1.1rem,3.6vw,2.6rem)] tracking-[0.3em] text-white/85 md:tracking-[0.62em]"
            >
              PHOTOGRAPHY
            </motion.span>
          </span>
        </h1>

        <div className="mt-10 flex flex-col gap-10 md:mt-14 md:flex-row md:items-end md:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
          >
            <p className="font-script text-3xl leading-tight text-accentlight md:text-5xl">
              Let&rsquo;s capture
            </p>
            <p className="font-script text-3xl leading-tight text-accentlight md:text-5xl">
              your next game.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="#booking"
              data-testid="hero-book-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#booking");
              }}
              className="rounded-full bg-gradient-to-r from-accent to-accentlight px-8 py-4 text-sm font-bold uppercase tracking-wider text-ink transition-all duration-300 hover:scale-[1.04] hover:brightness-110"
            >
              Book a game
            </a>
            <a
              href="#work"
              data-testid="hero-work-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#work");
              }}
              className="rounded-full border border-white/25 px-8 py-4 text-sm font-bold uppercase tracking-wider text-white/90 transition-all duration-300 hover:border-accentlight/70 hover:text-accentlight"
            >
              See the work
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 flex items-center gap-6 text-sm text-white/60 md:mt-20"
        >
          <a
            href="https://instagram.com/gamelensphotos"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-instagram-link"
            className="flex items-center gap-2 transition-colors hover:text-accentlight"
          >
            <Instagram className="h-4 w-4" /> @gamelensphotos
          </a>
          <a
            href="https://www.facebook.com/profile.php?id=61594933152311"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-facebook-link"
            className="flex items-center gap-2 transition-colors hover:text-accentlight"
          >
            <Facebook className="h-4 w-4" /> Facebook
          </a>
          <a
            href="mailto:GameLensPhotos@gmail.com"
            data-testid="hero-email-link"
            className="hidden items-center gap-2 transition-colors hover:text-accentlight sm:flex"
          >
            <Mail className="h-4 w-4" /> GameLensPhotos@gmail.com
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        onClick={() => scrollToId("#work")}
        data-testid="hero-scroll-cue"
        aria-label="Scroll to the work"
        className="absolute bottom-8 right-8 z-10 hidden rounded-full border border-white/20 p-3 text-white/70 transition-colors hover:border-accentlight hover:text-accentlight md:block"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
}
